/**
 * Consent state for analytics cookies (GDPR) and the campaign code
 * (utils/acquisition.js).
 *
 * The stored record is { choice: 'accepted' | 'declined', timestamp, version }.
 * No record stored means the user has not chosen yet — no tracking may run,
 * nothing non-essential may be stored, and the consent banner must show.
 * Bump CONSENT_VERSION if what consent covers ever changes; old records are
 * then treated as "no choice".
 *
 * Versions: 1 — GA4 statistics. 2 (October 2026) — also the campaign code
 * (utm_* and ref) kept on the device; banner text says "besøksstatistikk".
 */

const STORAGE_KEY = 'coord-analytics-consent';

export const CONSENT_VERSION = 2;

export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_DECLINED = 'declined';

const consentListeners = new Set();
const bannerOpenListeners = new Set();

export function getConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (record?.version !== CONSENT_VERSION) return null;
    if (record.choice !== CONSENT_ACCEPTED && record.choice !== CONSENT_DECLINED) return null;
    return record;
  } catch (error) {
    // localStorage unavailable (private mode quirks) or corrupt record —
    // fail closed: no consent, no tracking.
    return null;
  }
}

export function setConsent(choice) {
  const record = {
    choice: choice === CONSENT_ACCEPTED ? CONSENT_ACCEPTED : CONSENT_DECLINED,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (error) {
    // Storage failed — the choice still applies for this page view.
  }
  consentListeners.forEach((listener) => listener(record));
  return record;
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(listener) {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

/** Ask the consent banner to reopen (Personvern §8, Konto → Innstillinger). */
export function openConsentBanner() {
  bannerOpenListeners.forEach((listener) => listener());
}

/** Used by the banner component. Returns an unsubscribe function. */
export function onConsentBannerOpen(listener) {
  bannerOpenListeners.add(listener);
  return () => bannerOpenListeners.delete(listener);
}
